/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

const IS_NODE: boolean =
	// The process object is not defined at all in the browser environment so it has to be checked like this beforehand
	// eslint-disable-next-line @typescript-eslint/prefer-optional-chain
	/* #__PURE__ -- @preserve */ ( () => !!( typeof process !== 'undefined' && process.versions && process.versions.node ) )();

// `as const` guarantees the structure and its readonly state. The type can be inferred
// while direct type definition requires more bloated code
// eslint-disable-next-line @typescript-eslint/typedef
export const MessageDataTypes = {
	BUFFER: 1,
	STRING: 2,
	NUMBER: 3,
	OBJECT: 4
} as const;

export type IMessageDataTypes = typeof MessageDataTypes;
export type MessageDataType = IMessageDataTypes[keyof IMessageDataTypes];

export default class ParserUtils {
	/**
	 * Generates packet type code (number) from types.
	 *
	 * Algorithm:
	 * 	INPUT: [STRING, OBJECT, BUFFER]
	 *  OUTPUT: 241
	 *  OUTPUT = INPUT[0] * 100 + INPUT[1] * 10 + INPUT[2] * 1
	 */
	public static getPacketType( ...types: MessageDataType[] ): number {
		let frameType: number = 0;

		for ( let i: number = 0; i < types.length; i++ ) {
			const type: number = types[ i ];

			frameType += type * Math.pow( 10, types.length - i - 1 );
		}

		return frameType;
	}

	/**
	 * Returns name of type which is supported by Parser.
	 */
	public static getType( obj: unknown ): MessageDataType {
		if ( ParserUtils.isBuffer( obj ) ) {
			return MessageDataTypes.BUFFER;
		}

		const type: string = typeof obj;

		if ( type === 'string' ) {
			return MessageDataTypes.STRING;
		}

		if ( type === 'number' ) {
			return MessageDataTypes.NUMBER;
		}

		return MessageDataTypes.OBJECT;
	}

	/**
	 * Returns information does object is a Buffer
	 */
	public static isBuffer( obj: unknown ): obj is Buffer {
		// instanceof operator can be safely used on node constructors e.g ArrayBuffer
		// eslint-disable-next-line no-restricted-syntax
		return ( IS_NODE && Buffer.isBuffer( obj ) ) || obj instanceof ArrayBuffer || obj instanceof Uint8Array ||
			this._isBufferView( obj );
	}

	private static _isBufferView( obj: unknown ): boolean {
		// instanceof operator can be safely used on node constructors e.g ArrayBuffer
		// eslint-disable-next-line no-restricted-syntax
		return typeof ArrayBuffer.isView === 'function' ? ArrayBuffer.isView( obj ) : ( ( obj as { buffer: unknown; } ).buffer instanceof
			ArrayBuffer );
	}
}
